import React, { useState, useEffect, ReactNode } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  IconButton,
  Snackbar,
  SelectChangeEvent,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  LocalFireDepartment as FireIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { backendApiService } from '../../services/backendApiService';
import { urlService } from '../../services/urlService';
import { keywordService } from '../../services/keywordService';
import { rankingService } from '../../services/rankingService';
import { fetchWebsites, setSelectedWebsite } from '../../store/slices/websiteSlice';
import { getCountryFlag, cleanUrl } from '../../utils/countryFlags';
// Removed react-markdown import - will use simple formatting instead

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`roast-tabpanel-${index}`}
      aria-labelledby={`roast-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

interface AnalysisResult {
  analysis: string;
  analysisType: string;
  url: string;
  timestamp: string;
}

const AIRoast: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);
  const websites = useSelector((state: RootState) => state.website.websites);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Form state
  const [selectedUrl, setSelectedUrl] = useState('');
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [includeKeywords, setIncludeKeywords] = useState(true);
  const [includeRankings, setIncludeRankings] = useState(true);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Data state
  const [urls, setUrls] = useState<string[]>([]);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [content, setContent] = useState<string>('');
  const [contentLoading, setContentLoading] = useState(false);
  const [contentData, setContentData] = useState<any>(null);
  
  // Analysis types matching backend
  const analysisTypes = [
    { id: 'content', name: 'Content Roast' },
    { id: 'competitor_comparison', name: 'Competitor Comparison' },
    { id: 'ranking_patterns', name: 'Ranking Patterns' },
    { id: 'meta_descriptions', name: 'Meta Descriptions' },
    { id: 'keyword_analysis', name: 'Keyword Analysis' },
    { id: 'content_recommendations', name: 'Content Recommendations' },
  ];

  // Load websites on component mount
  useEffect(() => {
    if (websites.length === 0) {
      dispatch(fetchWebsites());
    }
  }, [dispatch, websites.length]);

  // Load URLs when website changes
  useEffect(() => {
    const loadUrls = async () => {
      if (!selectedWebsite?.website_id) return;
      
      try {
        const { urls: urlData, error } = await urlService.getUrlsByWebsite(selectedWebsite.website_id);
        
        if (error) {
          console.error('Error loading URLs:', error);
          setError('Failed to load URLs');
        } else {
          // Extract the full URLs from the URL objects and deduplicate
          const urlList = urlData.map(url => url.full_url || url.url_id);
          const uniqueUrls = Array.from(new Set(urlList));
          setUrls(uniqueUrls);
          if (uniqueUrls.length > 0) {
            setSelectedUrl(uniqueUrls[0]);
          }
        }
      } catch (err) {
        console.error('Error loading URLs:', err);
        setError('Failed to load URLs');
      }
    };
    
    loadUrls();
  }, [selectedWebsite]);

  // Load content when URL changes
  useEffect(() => {
    const loadContent = async () => {
      if (!selectedUrl || !selectedWebsite) return;
      
      // Don't reload if we already have content for this URL
      if (contentData && contentData.url === selectedUrl) {
        console.log('[AIRoast] Content already loaded for this URL, skipping reload');
        return;
      }
      
      setContentLoading(true);
      setContent('');
      setContentData(null);
      
      try {
        const response = await backendApiService.post('/api/ai-roast/content', {
          url: selectedUrl
        });
        
        if (response && response.success && response.data) {
          setContentData(response.data);
          setContent(response.data.content || '');
          console.log('[AIRoast] Content loaded:', response.data.content ? response.data.content.length + ' characters' : 'No content');
        } else {
          console.warn('No content found for URL:', selectedUrl);
          setContent('');
        }
      } catch (err: any) {
        console.error('Error loading content:', err);
        if (err.response?.status === 404) {
          setContent('Content not found. Please ensure the URL exists and is published.');
        } else {
          setContent('Failed to load content from WordPress.');
        }
      } finally {
        setContentLoading(false);
      }
    };
    
    loadContent();
  }, [selectedUrl, selectedWebsite]);

  const generateRoast = async () => {
    if (!selectedUrl) {
      setError('Please select a URL to analyze');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const analysisType = analysisTypes[activeTab].id;
      
      // Prepare request data based on analysis type
      const requestData: any = {
        url: selectedUrl,
        analysisType,
      };

      // Add competitor URL if provided (for competitor comparison)
      if (competitorUrl && analysisType === 'competitor_comparison') {
        requestData.competitorUrl = competitorUrl;
        console.log('[AIRoast] Including competitor URL:', competitorUrl);
      }

      // Add content for content-based analyses
      if (['content', 'keyword_analysis', 'content_recommendations', 'competitor_comparison'].includes(analysisType)) {
        requestData.content = content;
        console.log('[AIRoast] Sending content to analyze:', content.substring(0, 200) + '...');
        console.log('[AIRoast] Content length:', content.length);
      }
      
      // Add keywords and rankings if requested
      if (includeKeywords && selectedWebsite?.website_id) {
        try {
          const { keywords } = await keywordService.getKeywordsByWebsite(selectedWebsite.website_id);
          // For now, just use all keywords for the website
          // In a real implementation, you might want to filter by URL
          requestData.keywords = keywords.map(kw => kw.keyword);
        } catch (err) {
          console.error('Error fetching keywords:', err);
        }
      }
      
      if (includeRankings && selectedWebsite?.website_id) {
        try {
          const { rankings } = await rankingService.getRankingsByWebsite(selectedWebsite.website_id);
          // Filter rankings for this URL
          const urlRankings = rankings.filter(r => r.ranking_url === selectedUrl);
          requestData.rankings = urlRankings.map(r => ({
            keyword: r.keyword,
            position: r.position,
            change: r.position_change || '0'
          }));
        } catch (err) {
          console.error('Error fetching rankings:', err);
        }
      }
      
      // Add meta description for meta analysis
      if (analysisType === 'meta_descriptions' && contentData) {
        requestData.metaDescription = contentData.meta_description || '';
      }
      
      console.log('[AIRoast] Sending request with data:', {
        ...requestData,
        content: requestData.content ? `${requestData.content.length} characters` : 'NO CONTENT'
      });
      
      const response = await backendApiService.post('/api/ai-roast/analyze', requestData);
      console.log('[AIRoast] Analysis response:', response);
      
      if (response && response.success) {
        setAnalysisResult(response.data);
        setSuccess('Analysis generated successfully!');
      } else {
        console.error('[AIRoast] Analysis failed:', response);
        setError(response?.error || 'Failed to generate analysis');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate AI roast');
    } finally {
      setLoading(false);
    }
  };

  const downloadAnalysis = () => {
    if (!analysisResult) return;
    
    // Convert the analysis content to properly formatted HTML
    const formattedContent = analysisResult.analysis
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');
    
    const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>AI Roast - ${analysisTypes[activeTab].name} - ${analysisResult.url}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body { 
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; 
      line-height: 1.6;
      color: #2c3e50;
      background: #f8f9fa;
    }
    
    .container {
      max-width: 900px;
      margin: 0 auto;
      background: white;
      min-height: 100vh;
      box-shadow: 0 0 20px rgba(0,0,0,0.05);
    }
    
    .header { 
      background: linear-gradient(135deg, #d61f26 0%, #8b0000 100%);
      color: white;
      padding: 40px;
      position: relative;
      overflow: hidden;
    }
    
    .header::before {
      content: "🔥";
      position: absolute;
      right: -20px;
      top: -20px;
      font-size: 150px;
      opacity: 0.1;
    }
    
    h1 { 
      font-size: 2.5em;
      margin-bottom: 20px;
      font-weight: 700;
    }
    
    .meta-info {
      background: rgba(255,255,255,0.1);
      border-radius: 8px;
      padding: 15px 20px;
      backdrop-filter: blur(10px);
    }
    
    .url { 
      font-family: 'Courier New', monospace;
      word-break: break-all;
      opacity: 0.9;
      font-size: 0.9em;
    }
    
    .timestamp { 
      opacity: 0.8;
      font-size: 0.85em; 
      margin-top: 8px;
    }
    
    .content {
      padding: 40px;
    }
    
    .analysis { 
      font-size: 1.05em;
      color: #2c3e50;
    }
    
    .analysis h2 { 
      color: #2c3e50; 
      font-size: 1.8em;
      margin: 35px 0 20px 0;
      padding-bottom: 10px;
      border-bottom: 2px solid #ecf0f1;
    }
    
    .analysis h3 { 
      color: #34495e; 
      font-size: 1.4em;
      margin: 25px 0 15px 0;
    }
    
    .analysis p {
      margin: 15px 0;
      line-height: 1.8;
    }
    
    .analysis strong { 
      color: #d61f26; 
      font-weight: 600;
    }
    
    .analysis em { 
      background: #fff3cd;
      padding: 1px 4px;
      border-radius: 3px;
      font-style: normal;
      font-weight: 500;
      color: #856404;
    }
    
    .analysis ul {
      margin: 20px 0;
      padding-left: 0;
      list-style: none;
    }
    
    .analysis li {
      margin: 12px 0;
      padding-left: 30px;
      position: relative;
      line-height: 1.8;
    }
    
    .analysis li::before {
      content: "→";
      position: absolute;
      left: 0;
      color: #d61f26;
      font-weight: bold;
      font-size: 1.2em;
    }
    
    .analysis code { 
      background: #f8f9fa; 
      padding: 3px 8px; 
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
      color: #e74c3c;
      border: 1px solid #dee2e6;
    }
    
    .analysis pre { 
      background: #2c3e50; 
      color: #ecf0f1;
      padding: 20px; 
      overflow-x: auto; 
      border-radius: 8px;
      font-size: 0.9em;
      line-height: 1.5;
      margin: 20px 0;
    }
    
    .footer {
      margin-top: 60px;
      padding: 30px 40px;
      border-top: 1px solid #ecf0f1;
      text-align: center;
      color: #95a5a6;
      font-size: 0.9em;
      background: #f8f9fa;
    }
    
    @media print {
      body { background: white; }
      .container { box-shadow: none; }
      .header { 
        background: #d61f26; 
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }
    }
    
    @media (max-width: 768px) {
      .header { padding: 30px 20px; }
      .content { padding: 30px 20px; }
      h1 { font-size: 2em; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🔥 AI Roast - ${analysisTypes[activeTab].name}</h1>
      <div class="meta-info">
        <div class="url">📍 ${analysisResult.url}</div>
        <div class="timestamp">📅 ${new Date(analysisResult.timestamp).toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}</div>
      </div>
    </div>
    <div class="content">
      <div class="analysis">
        <p>${formattedContent}</p>
      </div>
    </div>
    <div class="footer">
      <p><strong>Unicorn SEO</strong> AI Roast Report</p>
      <p>Generated with 🔥 by our brutally honest AI • ${new Date().getFullYear()}</p>
    </div>
  </div>
</body>
</html>
    `;
    
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-roast-${analysisTypes[activeTab].id}-${Date.now()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FireIcon sx={{ color: '#d61f26' }} />
          AI Roast
        </Typography>
        
        
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Ready for a Brutal SEO Roast?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Our sassy AI is about to tear apart your SEO performance with zero filter!
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            We'll analyze your content, keywords, and rankings to reveal the good, bad, and ugly of your SEO strategy.
            Our AI doesn't sugarcoat feedback - it gives it to you straight with specific fixes!
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 'medium' }}>
            Choose what you want roasted below!
          </Typography>
        </Paper>
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select Website
        </Typography>
        <Autocomplete
          value={selectedWebsite}
          onChange={(event, newValue) => {
            if (newValue) {
              dispatch(setSelectedWebsite(newValue));
            }
          }}
          options={websites}
          getOptionLabel={(option) => {
            const flag = getCountryFlag(option.country);
            const domain = cleanUrl(option.base_url);
            return `${flag} ${domain}`;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Search for a website..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Typography>
                {getCountryFlag(option.country)} {cleanUrl(option.base_url)}
              </Typography>
            </Box>
          )}
          sx={{ width: '100%' }}
          isOptionEqualToValue={(option, value) => option.website_id === value.website_id}
        />
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {analysisTypes.map((type, index) => (
            <Tab
              key={type.id}
              label={type.name}
            />
          ))}
        </Tabs>
        
        {/* Content Selection Panel */}
        <Box sx={{ mt: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Content Selection
              </Typography>
              
              <Autocomplete
                fullWidth
                sx={{ mb: 2 }}
                value={selectedUrl || null}
                onChange={(event, newValue) => {
                  setSelectedUrl(newValue || '');
                }}
                options={urls}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select URL to Analyze"
                    variant="outlined"
                    placeholder={!selectedWebsite ? 'Select a website first' : 'Type to search URLs...'}
                  />
                )}
                noOptionsText="No URLs found"
                isOptionEqualToValue={(option, value) => option === value}
              />
              
              {/* Competitor URL field - only show for Competitor Comparison tab */}
              {analysisTypes[activeTab]?.id === 'competitor_comparison' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🏆 Competitor URL (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    value={competitorUrl}
                    onChange={(e) => setCompetitorUrl(e.target.value)}
                    placeholder="https://competitor-site.com/their-ranking-page"
                    variant="outlined"
                    helperText="Enter a competitor URL to compare. We'll analyze why they might be ranking higher and what you can learn from them."
                    sx={{ mb: 2 }}
                  />
                  {competitorUrl && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Competitor URL set! We'll fetch and compare both pages to show you exactly why they outrank you.
                    </Alert>
                  )}
                </Box>
              )}

              {selectedUrl && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Selected URL: {selectedUrl}
                  </Alert>
                  
                  {contentLoading && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <CircularProgress size={30} />
                      <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                        Loading content from WordPress...
                      </Typography>
                    </Box>
                  )}
                  
                  {!contentLoading && contentData && (
                    <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Content Loaded:
                      </Typography>
                      {contentData.title && (
                        <Typography variant="body2">
                          <strong>Title:</strong> {contentData.title}
                        </Typography>
                      )}
                      {contentData.wordCount > 0 && (
                        <Typography variant="body2">
                          <strong>Word Count:</strong> {contentData.wordCount} words
                        </Typography>
                      )}
                      {contentData.meta_description && (
                        <Typography variant="body2">
                          <strong>Meta Description:</strong> {contentData.meta_description}
                        </Typography>
                      )}
                      {content && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>Content Preview:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            mt: 0.5, 
                            p: 1, 
                            backgroundColor: 'white', 
                            borderRadius: 1,
                            maxHeight: '100px',
                            overflow: 'auto',
                            fontSize: '0.875rem',
                            color: 'text.secondary'
                          }}>
                            {content.substring(0, 500)}...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {!contentLoading && !contentData && selectedUrl && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Content not found for this URL. The roast will be limited without content data.
                    </Alert>
                  )}
                </>
              )}
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeKeywords}
                      onChange={(e) => setIncludeKeywords(e.target.checked)}
                    />
                  }
                  label="Include keyword data in analysis"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeRankings}
                      onChange={(e) => setIncludeRankings(e.target.checked)}
                    />
                  }
                  label="Include ranking data in analysis"
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
        
        {/* Tab Panels */}
        {analysisTypes.map((type, index) => (
          <TabPanel key={type.id} value={activeTab} index={index}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" gutterBottom>
                {type.name}
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {getAnalysisDescription(type.id)}
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                onClick={generateRoast}
                disabled={loading || !selectedUrl || contentLoading}
                startIcon={loading ? <CircularProgress size={20} /> : <FireIcon />}
                sx={{
                  backgroundColor: '#d61f26',
                  '&:hover': { backgroundColor: '#b71c1c' },
                }}
              >
                {loading ? 'Generating Roast...' : `Generate ${type.name}`}
              </Button>
              
              {!selectedUrl && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  Please select a URL above to start the roast
                </Typography>
              )}
              
              {contentLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading content data...
                </Typography>
              )}
            </Box>
          </TabPanel>
        ))}
        
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'relative',
              mt: 4,
              p: 4,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Generating Analysis...
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary">
              Our AI is preparing your SEO roast
            </Typography>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              This may take a few moments
            </Typography>
          </Box>
        )}

        {/* Analysis Result */}
        {analysisResult && !loading && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">
                Analysis Result
              </Typography>
              <Box>
                <IconButton onClick={generateRoast} title="Regenerate">
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={downloadAnalysis} title="Download">
                  <DownloadIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Paper 
              sx={{ 
                p: 0, 
                backgroundColor: '#ffffff',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Box sx={{ 
                p: 4,
                '& h1': {
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#d32f2f',
                  mb: 3,
                  textAlign: 'center',
                  borderBottom: '3px solid #ff5252',
                  pb: 2,
                },
                '& h2': {
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: '#f57c00',
                  mt: 4,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                },
                '& h3': {
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  color: '#424242',
                  mt: 3,
                  mb: 1.5,
                },
                '& p': {
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: '#616161',
                  mb: 2,
                },
                '& ul, & ol': {
                  pl: 3,
                  mb: 2,
                  '& li': {
                    fontSize: '1rem',
                    lineHeight: 1.8,
                    color: '#616161',
                    mb: 1,
                    '&::marker': {
                      color: '#ff5252',
                    }
                  }
                },
                '& strong': {
                  color: '#212121',
                  fontWeight: 600,
                },
                '& code': {
                  backgroundColor: '#f5f5f5',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                  color: '#d32f2f',
                },
                '& pre': {
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px',
                  overflow: 'auto',
                  mb: 2,
                  '& code': {
                    backgroundColor: 'transparent',
                    padding: 0,
                    color: '#424242',
                  }
                },
                '& blockquote': {
                  borderLeft: '4px solid #ff5252',
                  pl: 2,
                  ml: 0,
                  fontStyle: 'italic',
                  color: '#757575',
                }
              }}>
                {formatAnalysisContent(analysisResult.analysis)}
              </Box>
            </Paper>
          </Box>
        )}
      </Paper>
      
      {/* Error/Success Messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function getAnalysisDescription(analysisType: string): string {
  const descriptions: Record<string, string> = {
    content: "Our AI will analyze your content and provide specific, actionable improvements with no sugarcoating.",
    competitor_comparison: "Compare your page against a competitor's URL. Find out WHY they're ranking higher and get a battle plan to outrank them!",
    ranking_patterns: "Discover ranking volatility issues and get targeted fixes to stabilize your search positions.",
    meta_descriptions: "Get a comprehensive meta description audit with specific rewrites that drive more clicks.",
    keyword_analysis: "Identify keyword targeting mistakes and discover untapped opportunities in your niche.",
    content_recommendations: "Receive strategic content recommendations based on gaps and competitive analysis.",
  };

  return descriptions[analysisType] || "Get ready for honest SEO feedback with specific fixes.";
}

function formatAnalysisContent(analysis: string): React.ReactNode {
  // Split content into lines for processing
  const lines = analysis.split('\n');
  const elements: React.ReactNode[] = [];
  let currentList: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let inCodeBlock = false;
  let codeLines: string[] = [];
  
  const processLine = (line: string, index: number) => {
    // Handle code blocks
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${index}`}>
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        codeLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      return;
    }
    
    if (inCodeBlock) {
      codeLines.push(line);
      return;
    }
    
    // Handle lists
    const unorderedMatch = line.match(/^[\s]*[-*]\s+(.+)/);
    const orderedMatch = line.match(/^[\s]*\d+\.\s+(.+)/);
    
    if (unorderedMatch || orderedMatch) {
      const content = unorderedMatch ? unorderedMatch[1] : orderedMatch![1];
      const newListType = unorderedMatch ? 'ul' : 'ol';
      
      if (listType !== newListType && currentList.length > 0 && listType) {
        // End previous list
        if (listType === 'ul') {
          elements.push(
            <ul key={`list-${index}`}>
              {currentList.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: processInlineFormatting(item) }} />
              ))}
            </ul>
          );
        } else {
          elements.push(
            <ol key={`list-${index}`}>
              {currentList.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: processInlineFormatting(item) }} />
              ))}
            </ol>
          );
        }
        currentList = [];
      }
      
      listType = newListType;
      currentList.push(content);
      return;
    } else if (currentList.length > 0 && listType) {
      // End current list
      if (listType === 'ul') {
        elements.push(
          <ul key={`list-${index}`}>
            {currentList.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: processInlineFormatting(item) }} />
            ))}
          </ul>
        );
      } else {
        elements.push(
          <ol key={`list-${index}`}>
            {currentList.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: processInlineFormatting(item) }} />
            ))}
          </ol>
        );
      }
      currentList = [];
      listType = null;
    }
    
    // Handle headers
    const h1Match = line.match(/^#\s+(.+)/);
    const h2Match = line.match(/^##\s+(.+)/);
    const h3Match = line.match(/^###\s+(.+)/);
    
    if (h1Match) {
      elements.push(
        <h1 key={`h1-${index}`} dangerouslySetInnerHTML={{ __html: processInlineFormatting(h1Match[1]) }} />
      );
    } else if (h2Match) {
      elements.push(
        <h2 key={`h2-${index}`} dangerouslySetInnerHTML={{ __html: processInlineFormatting(h2Match[1]) }} />
      );
    } else if (h3Match) {
      elements.push(
        <h3 key={`h3-${index}`} dangerouslySetInnerHTML={{ __html: processInlineFormatting(h3Match[1]) }} />
      );
    } else if (line.trim()) {
      // Regular paragraph
      elements.push(
        <p key={`p-${index}`} dangerouslySetInnerHTML={{ __html: processInlineFormatting(line) }} />
      );
    }
  };
  
  // Process inline formatting
  const processInlineFormatting = (text: string): string => {
    return text
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic (but not if it's part of bold)
      .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
      // Inline code
      .replace(/`(.+?)`/g, '<code>$1</code>');
  };
  
  // Process all lines
  lines.forEach((line, index) => processLine(line, index));
  
  // Handle any remaining list
  if (currentList.length > 0 && listType) {
    if (listType === 'ul') {
      elements.push(
        <ul key="final-list">
          {currentList.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: processInlineFormatting(item) }} />
          ))}
        </ul>
      );
    } else {
      elements.push(
        <ol key="final-list">
          {currentList.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: processInlineFormatting(item) }} />
          ))}
        </ol>
      );
    }
  }
  
  return <>{elements}</>;
}

export default AIRoast;